import { useState } from 'react'
import type { FieldDefinition } from '@/api/types'

/** Persisted settings for the board's card fields (localStorage 'pp.cardFields'). */

export const CARD_FIELDS_STORAGE_KEY = 'pp.cardFields'

/** HubSpot-style cap on visible card fields (title and status badge don't count). */
export const MAX_CARD_FIELDS = 6

/** Prefix for deal custom-field ids on cards, e.g. 'custom.bedrooms'. */
export const CUSTOM_FIELD_PREFIX = 'custom.'

export interface CardFieldDef {
  id: string
  label: string
}

/** Built-in card field pool. Title and status badge are always shown and not configurable. */
export const BUILTIN_CARD_FIELDS: CardFieldDef[] = [
  { id: 'value', label: 'Value' },
  { id: 'client', label: 'Client' },
  { id: 'lastTouch', label: 'Last contact' },
  { id: 'nextContactDate', label: 'Next contact date' },
  { id: 'owner', label: 'Owner' },
  { id: 'created', label: 'Created' },
]

/** Deal custom fields as card field defs ('custom.<key>' ids, labels from definitions). */
export function customCardFields(defs: FieldDefinition[] | undefined): CardFieldDef[] {
  return (defs ?? []).map((d) => ({ id: CUSTOM_FIELD_PREFIX + d.fieldKey, label: d.label }))
}

export interface CardSettings {
  /** Visible card field ids in display order (built-in ids or 'custom.<key>'). */
  fields: string[]
  /** Show the deal's photo across the top of its card (doesn't count toward the field cap). */
  showPhoto: boolean
}

export const DEFAULT_CARD_SETTINGS: CardSettings = {
  fields: ['value', 'client', 'nextContactDate', 'lastTouch'],
  showPhoto: true,
}

/** Coerces parsed JSON of unknown shape to valid settings, falling back on the default. */
function normalize(raw: unknown): CardSettings {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const requested = Array.isArray(obj.fields) ? (obj.fields as unknown[]) : DEFAULT_CARD_SETTINGS.fields
  const fields: string[] = []
  for (const id of requested) {
    if (typeof id !== 'string' || fields.includes(id)) continue
    // Custom fields whose definition was deleted are dropped at render time.
    if (!id.startsWith(CUSTOM_FIELD_PREFIX) && !BUILTIN_CARD_FIELDS.some((f) => f.id === id)) continue
    fields.push(id)
    if (fields.length >= MAX_CARD_FIELDS) break
  }
  // Settings saved before photos existed have no flag: photos show (switch them off in Card settings).
  return { fields, showPhoto: obj.showPhoto !== false }
}

export function loadCardSettings(): CardSettings {
  try {
    const raw = localStorage.getItem(CARD_FIELDS_STORAGE_KEY)
    if (!raw) return DEFAULT_CARD_SETTINGS
    return normalize(JSON.parse(raw))
  } catch {
    return DEFAULT_CARD_SETTINGS
  }
}

export function saveCardSettings(settings: CardSettings) {
  localStorage.setItem(CARD_FIELDS_STORAGE_KEY, JSON.stringify(settings))
}

/** Card-field settings backed by localStorage: [settings, update]. */
export function useCardSettings(): [CardSettings, (next: CardSettings) => void] {
  const [settings, setSettings] = useState<CardSettings>(loadCardSettings)
  function update(next: CardSettings) {
    setSettings(next)
    saveCardSettings(next)
  }
  return [settings, update]
}
